import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Loader2, Warehouse, PackageX, TrendingUp, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FunnelTriggerButton from "@/components/FunnelTriggerButton";
import MagazzinoFunnelDialog from "@/components/MagazzinoFunnelDialog";
import { toast } from "sonner";

const eur = (v) => `€ ${Math.round(Number(v) || 0).toLocaleString("it-IT")}`;
const fmtIt = (iso) => { const p = String(iso || "").slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };

export default function StimaMagazzino() {
  const _n = new Date();
  const _next = new Date(_n.getFullYear(), _n.getMonth() + 1, 1);
  const minDate = `${_next.getFullYear()}-${String(_next.getMonth() + 1).padStart(2, "0")}-01`;
  const [data, setData] = useState(minDate);
  const [res, setRes] = useState(null);
  const [forecast, setForecast] = useState(false);
  const [incremento, setIncremento] = useState(0);
  const [baseMesi, setBaseMesi] = useState(6);
  const [allocations, setAllocations] = useState({});
  const [savedAlloc, setSavedAlloc] = useState({});
  const [allocOpen, setAllocOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [includiZero, setIncludiZero] = useState(false);
  const [extraBusy, setExtraBusy] = useState(false);
  const [funnelOpen, setFunnelOpen] = useState(false);

  useEffect(() => {
    setRes(null);
    const t = setTimeout(() => {
      const params = { data };
      if (forecast) { params.forecast = true; params.incremento = Number(incremento) || 0; params.base_mesi = baseMesi; }
      if (includiZero) params.includi_zero = true;
      api.get("/magazzino/stima", { params }).then((r) => setRes(r.data)).catch(() => setRes(false));
    }, 300);
    return () => clearTimeout(t);
  }, [data, forecast, incremento, baseMesi, includiZero]);

  const lis = res && res.listino ? res.listino : null;
  const ext = res && res.extra ? res.extra : null;
  const negatives = lis ? lis.articoli.filter((a) => a.giac_data < 0) : [];
  const byCode = {};
  (lis ? lis.articoli : []).forEach((a) => { byCode[a.codice] = a; });
  const adjGiac = {};
  (lis ? lis.articoli : []).forEach((a) => { adjGiac[a.codice] = a.giac_data; });
  const notes = {};
  negatives.forEach((a) => {
    const tgt = allocations[a.codice];
    if (tgt && byCode[tgt]) {
      const deficit = -a.giac_data;
      adjGiac[tgt] = adjGiac[tgt] - deficit;
      adjGiac[a.codice] = 0;
      const moved = deficit * (byCode[tgt].costo_unitario || 0);
      (notes[a.codice] = notes[a.codice] || []).push(`Scoperto di ${deficit} pz coperto da ${tgt}`);
      (notes[tgt] = notes[tgt] || []).push(`${deficit} pz destinati a ${a.codice} (−${eur(moved)})`);
    }
  });
  const rowValData = (a) => Math.max(0, adjGiac[a.codice]) * (a.costo_unitario || 0);
  const totalDataAdj = (lis ? lis.articoli : []).reduce((s, a) => s + rowValData(a), 0);
  const deltaCostoAdj = lis ? totalDataAdj - lis.tot.costo_oggi : 0;
  const fiscOggi = res && res.fiscale && res.fiscale.valore_costo != null ? res.fiscale.valore_costo : null;
  const listinoOggi = lis ? lis.tot.costo_oggi : 0;
  const extraCost = fiscOggi != null ? Math.max(0, fiscOggi - listinoOggi) : 0;
  const stimaFiscData = totalDataAdj + extraCost;
  const catGroups = [];
  (lis ? lis.articoli : []).forEach((a) => {
    const cat = a.categoria || "Senza categoria";
    let g = catGroups.find((x) => x.categoria === cat);
    if (!g) { g = { categoria: cat, arts: [] }; catGroups.push(g); }
    g.arts.push(a);
  });

  useEffect(() => {
    api.get("/magazzino/allocations").then((r) => setSavedAlloc(r.data?.map || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (lis && negatives.length > 0) {
      const pre = {};
      negatives.forEach((a) => { if (savedAlloc[a.codice] && byCode[savedAlloc[a.codice]]) pre[a.codice] = savedAlloc[a.codice]; });
      setAllocations(pre);
      setAllocOpen(negatives.some((a) => !pre[a.codice]));
    } else { setAllocations({}); setAllocOpen(false); }
  }, [res, savedAlloc]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistAlloc = async (next) => {
    const merged = { ...savedAlloc };
    negatives.forEach((a) => { if (next[a.codice]) merged[a.codice] = next[a.codice]; else delete merged[a.codice]; });
    setSavedAlloc(merged);
    try { await api.put("/magazzino/allocations", { map: merged }); toast.success("Associazioni salvate"); }
    catch { toast.error("Salvataggio associazioni non riuscito"); }
  };

  const revertAlloc = () => {
    const pre = {};
    negatives.forEach((a) => { if (savedAlloc[a.codice] && byCode[savedAlloc[a.codice]]) pre[a.codice] = savedAlloc[a.codice]; });
    setAllocations(pre); setAllocOpen(false);
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const body = { data };
      if (forecast) { body.forecast = true; body.incremento = Number(incremento) || 0; body.base_mesi = baseMesi; }
      if (includiZero) body.includi_zero = true;
      const alloc = {};
      negatives.forEach((a) => { if (allocations[a.codice]) alloc[a.codice] = allocations[a.codice]; });
      body.allocations = alloc;
      const r = await api.post("/magazzino/stima/pdf", body, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const el = document.createElement("a");
      el.href = url; el.download = `stima-magazzino-${data}.pdf`; el.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download PDF non riuscito"); } finally { setPdfBusy(false); }
  };

  const downloadExtra = async () => {
    setExtraBusy(true);
    try {
      const r = await api.post("/magazzino/extra-listino/pdf", {}, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const el = document.createElement("a");
      el.href = url; el.download = "magazzino-extra-listino.pdf"; el.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download extra-listino non riuscito"); } finally { setExtraBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="stima-magazzino-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold"><Warehouse className="h-6 w-6 text-primary" /> Stima magazzino</h1>
        <p className="mt-1 text-sm text-muted-foreground">Valorizzazione basata <span className="font-medium">sempre ed esclusivamente</span> sul costo rilevato dal CSV del magazzino fiscale (mai listini né costi a listino). Valore del magazzino a catalogo oggi e proiettato a una data (dai movimenti: arrivi − impegni); a parte, il valore totale dell'intero magazzino fiscale.</p>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="data" className="text-sm font-medium text-muted-foreground">Stima alla data</Label>
            <Input id="data" type="date" value={data} min={minDate} onChange={(e) => setData(e.target.value && e.target.value >= minDate ? e.target.value : minDate)} className="h-9 w-44" data-testid="stima-date" />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="fc" checked={forecast} onCheckedChange={setForecast} data-testid="stima-forecast-toggle" />
            <Label htmlFor="fc" className="flex items-center gap-1.5 text-sm"><TrendingUp className="h-4 w-4 text-amber-500" /> Includi stima uscite</Label>
          </div>
          {forecast && (
            <>
              <div className="flex items-center gap-2">
                <Label htmlFor="inc" className="text-xs font-medium text-muted-foreground">Incremento %</Label>
                <Input id="inc" type="number" value={incremento} onChange={(e) => setIncremento(e.target.value)} className="h-9 w-24" data-testid="stima-incremento" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Media su:</span>
                <div className="flex overflow-hidden rounded-lg border border-border">
                  <Button variant={baseMesi === 6 ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setBaseMesi(6)} data-testid="stima-base-6">6 mesi</Button>
                  <Button variant={baseMesi === 12 ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setBaseMesi(12)} data-testid="stima-base-12">12 mesi</Button>
                </div>
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <Switch id="zero" checked={includiZero} onCheckedChange={setIncludiZero} data-testid="stima-zero-toggle" />
            <Label htmlFor="zero" className="text-sm">Mostra giacenza 0</Label>
          </div>
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy || !res} className="ml-auto" data-testid="stima-pdf-btn">
            {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} PDF stima
          </Button>
          <Button variant="outline" size="sm" onClick={downloadExtra} disabled={extraBusy} data-testid="stima-extra-pdf-btn">
            {extraBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} PDF extra-listino
          </Button>
          <FunnelTriggerButton label="Funnel" onClick={() => setFunnelOpen(true)} data-testid="stima-funnel-btn" />
        </div>
        {forecast && (
          <p className="mt-3 text-[11px] text-amber-600 dark:text-amber-400" data-testid="stima-forecast-note">
            Alla giacenza proiettata (arrivi − impegni) viene sottratta una stima di uscite basata sulla media dei movimenti in uscita degli ultimi {baseMesi} mesi{Number(incremento) ? ` maggiorata del ${incremento}%` : ""}, proporzionale ai giorni fino alla data.
          </p>
        )}
      </Card>

      {res === null ? (
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground" data-testid="stima-loading"><Loader2 className="h-4 w-4 animate-spin" /> Calcolo la stima…</div>
      ) : res === false ? (
        <div className="p-6 text-sm text-red-500" data-testid="stima-error">Errore nel recupero dei dati.</div>
      ) : (
        <>
          {res.forecast && (
            <Badge variant="outline" data-testid="stima-storico-badge" className={res.storico?.attivo ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : "border-amber-500/50 text-amber-500"}>
              {res.storico?.attivo ? `Storico consumi: ${res.storico.mesi} mesi · agg. ${fmtIt(res.storico.aggiornato_al)}` : "Storico non ancora popolato · stima su impegni"}
            </Badge>
          )}
          <Card className="p-4" data-testid="stima-card-extra">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Magazzino fiscale {res.fiscale?.data ? `(${res.fiscale.data})` : "(oggi)"} · fonte CSV</div>
                <div className="mt-1 text-2xl font-bold text-foreground" data-testid="stima-fisc-oggi">{fiscOggi != null ? eur(fiscOggi) : "—"}</div>
                {fiscOggi != null && <div className="text-[11px] text-muted-foreground">a costo (CSV) · {res.fiscale.articoli} art.</div>}
                <div className="mt-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Magazzino articoli a listino (oggi)</div>
                <div className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="stima-listino-oggi">{eur(listinoOggi)}</div>
                <div className="text-[11px] text-muted-foreground">a costo (CSV) · {lis.n} art.</div>
              </div>
              <div className="sm:border-l sm:border-border sm:pl-4">
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Magazzino stimato al {fmtIt(res.data)}</div>
                <div className="mt-1 flex items-baseline gap-2"><span className="text-xl font-bold" data-testid="stima-listino-data">{eur(totalDataAdj)}</span><span className="text-[11px] text-muted-foreground">stima articoli a listino</span></div>
                <div className="mt-1 flex items-baseline gap-2"><span className="text-base font-semibold text-amber-600 dark:text-amber-400" data-testid="stima-extra-cost">+ {eur(extraCost)}</span><span className="text-[11px] text-muted-foreground">valore articoli extra-listino</span></div>
                <div className="mt-2 border-t border-border pt-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Stima magazzino fiscale al {fmtIt(res.data)}</div>
                <div className="mt-1 text-2xl font-bold text-foreground" data-testid="stima-fisc-data">{eur(stimaFiscData)}</div>
                {negatives.length > 0 && (
                  <button type="button" onClick={() => setAllocOpen(true)} className="mt-2 text-[11px] font-medium text-primary underline" data-testid="stima-alloc-reopen">Colloca gli scoperti su altri articoli</button>
                )}
              </div>
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">Nota:</span> il magazzino <b>extra-listino</b> ({eur(extraCost)}) è considerato una <b>costante</b>: viene mantenuto al valore odierno e non proiettato alla data. Tutti i valori sono a costo, sempre dalla fonte CSV del magazzino fiscale.
            </div>
          </Card>

          <p className="text-[11px] text-muted-foreground" data-testid="stima-cost-source-note">
            <span className="font-medium text-foreground">Nota:</span> nelle analisi di magazzino il costo è imputato <span className="font-medium">sempre ed esclusivamente</span> dal costo rilevato nel CSV del magazzino fiscale. Non vengono mai usati i listini né i costi a listino. Il prezzo di vendita non è riportato perché non pertinente alla valorizzazione di magazzino.
          </p>

          <div>
            <h2 className="mb-3 font-heading text-lg font-bold">Magazzino a catalogo <Badge variant="secondary">{lis.n} art.</Badge></h2>
            {catGroups.length === 0 ? (
              <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="stima-cat-empty"><PackageX className="mx-auto mb-2 h-6 w-6" /> Nessun articolo a catalogo con giacenza.</Card>
            ) : (
              <Accordion type="multiple" defaultValue={catGroups.map((g) => g.categoria)} className="space-y-3">
                {catGroups.map((g) => {
                  const nScop = g.arts.filter((a) => adjGiac[a.codice] < 0).length;
                  return (
                  <AccordionItem key={g.categoria} value={g.categoria} className="rounded-xl border border-border bg-card px-4" data-testid={`stima-cat-${g.categoria}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex flex-1 flex-wrap items-center gap-2 pr-3">
                        <span className="font-heading text-base font-bold">{g.categoria}</span>
                        <Badge variant="secondary">{g.arts.length} art.</Badge>
                        {nScop > 0 && <Badge variant="outline" className="border-red-500/50 text-red-500">{nScop} scoperti</Badge>}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-xs">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="py-2 pr-3 text-left font-medium">Articolo</th>
                              <th className="px-2 py-2 text-right font-medium">Giac. oggi</th>
                              <th className="px-2 py-2 text-right font-medium">Valore oggi</th>
                              <th className="px-2 py-2 text-right font-medium">Giac. al {fmtIt(res.data)}</th>
                              <th className="px-2 py-2 text-right font-medium">Valore al {fmtIt(res.data)}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.arts.map((a) => {
                              const gd = adjGiac[a.codice];
                              const ns = notes[a.codice] || [];
                              const isZero = (a.giac_oggi || 0) === 0 && gd === 0;
                              return (
                              <tr key={a.codice} className={`border-t border-border/50 ${isZero ? "text-muted-foreground" : ""}`} data-testid={`stima-art-${a.codice}`}>
                                <td className="py-2 pr-3">
                                  <span className="font-mono text-xs font-bold">{a.codice}</span>
                                  <div className="max-w-[280px] truncate text-[11px] text-muted-foreground">{a.descrizione}</div>
                                  {ns.map((t, i) => (<div key={i} className="text-[10px] text-amber-600 dark:text-amber-400">↔ {t}</div>))}
                                </td>
                                <td className="px-2 py-2 text-right font-medium">{a.giac_oggi}</td>
                                <td className="px-2 py-2 text-right text-emerald-600 dark:text-emerald-400">{eur(a.val_costo_oggi)}</td>
                                <td className={`px-2 py-2 text-right font-medium ${gd < 0 ? "text-red-500" : ""}`}>{gd}</td>
                                <td className="px-2 py-2 text-right">{eur(rowValData(a))}</td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </div>

          {ext.n > 0 && (
            <Accordion type="single" collapsible className="rounded-xl border border-amber-500/30 bg-card px-4">
              <AccordionItem value="extra" className="border-0" data-testid="stima-extra-item">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex flex-1 items-center gap-2 pr-3">
                    <Warehouse className="h-4 w-4 text-amber-500" />
                    <span className="font-heading text-base font-bold">Magazzino extra-listino</span>
                    <Badge variant="outline" className="border-amber-500/50 text-amber-500">{ext.n} art. · {ext.tot.pezzi_oggi} pz</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="mb-2 text-[11px] text-muted-foreground">Articoli presenti nel magazzino fiscale (acquisito ogni notte) ma non a listino: non hanno un prezzo, quindi si riporta solo la quantità.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-xs">
                      <thead>
                        <tr className="text-muted-foreground">
                          <th className="py-2 pr-3 text-left font-medium">Articolo</th>
                          <th className="px-2 py-2 text-right font-medium">Giac. oggi</th>
                          <th className="px-2 py-2 text-right font-medium">Giac. al {fmtIt(res.data)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ext.articoli.map((a) => (
                          <tr key={a.codice} className="border-t border-border/50" data-testid={`stima-extra-${a.codice}`}>
                            <td className="py-2 pr-3 font-mono text-xs font-bold">{a.codice}</td>
                            <td className="px-2 py-2 text-right font-medium">{a.giac_oggi}</td>
                            <td className={`px-2 py-2 text-right ${a.giac_data < 0 ? "text-red-500" : ""}`}>{a.giac_data}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </>
      )}

      <Dialog open={allocOpen} onOpenChange={setAllocOpen}>
        <DialogContent className="max-w-lg" data-testid="stima-alloc-dialog">
          <DialogHeader>
            <DialogTitle>Prodotti sotto zero al {fmtIt(res?.data)}</DialogTitle>
            <DialogDescription>Per ogni articolo in negativo puoi collocare il fabbisogno su un altro articolo <b>della stessa categoria</b> disponibile. Le associazioni vengono <b>salvate</b> e riproposte le volte successive: ti verranno richiesti solo gli scoperti non ancora associati. Il valore verrà scalato dall'articolo scelto.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            {negatives.map((a) => {
              const cands = (lis ? lis.articoli : []).filter((t) => t.categoria === a.categoria && t.codice !== a.codice && t.giac_data >= (-a.giac_data));
              return (
                <div key={a.codice} className="rounded border border-border p-2" data-testid={`stima-alloc-row-${a.codice}`}>
                  <div className="flex items-center gap-2 font-mono text-xs font-bold">{a.codice} <span className="text-red-500">({a.giac_data})</span>
                    {savedAlloc[a.codice] && <Badge variant="outline" className="border-emerald-500/50 text-[10px] text-emerald-600 dark:text-emerald-400">salvata</Badge>}
                  </div>
                  <div className="mb-1 text-[11px] text-muted-foreground">{a.categoria} · fabbisogno {-a.giac_data} pz</div>
                  <Select value={allocations[a.codice] || "__none__"} onValueChange={(v) => setAllocations((p) => ({ ...p, [a.codice]: v === "__none__" ? "" : v }))}>
                    <SelectTrigger className="h-9" data-testid={`stima-alloc-select-${a.codice}`}><SelectValue placeholder="Non collocare" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Non collocare</SelectItem>
                      {cands.map((t) => (<SelectItem key={t.codice} value={t.codice}>{t.codice} · disp. {t.giac_data}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {cands.length === 0 && <div className="mt-1 text-[11px] text-amber-500">Nessun articolo della stessa categoria con disponibilità sufficiente a coprire il fabbisogno.</div>}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={revertAlloc} data-testid="stima-alloc-skip">Annulla</Button>
            <Button onClick={() => { persistAlloc(allocations); setAllocOpen(false); }} data-testid="stima-alloc-apply">Salva associazioni</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MagazzinoFunnelDialog open={funnelOpen} onOpenChange={setFunnelOpen} />
    </div>
  );
}
